import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { BrandFontFamilySlot } from "./use-kit";

const key = (brandId: string) => ["font-families", brandId] as const;

export function useFontFamilies(brandId: string) {
  return useQuery({
    queryKey: key(brandId),
    queryFn: () => apiFetch<BrandFontFamilySlot[]>(`/brands/${brandId}/font-families`),
    enabled: !!brandId,
  });
}

export function useCreateFontFamily(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; source: "GOOGLE" | "CUSTOM" }) =>
      apiFetch<BrandFontFamilySlot>(`/brands/${brandId}/font-families`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(brandId) }),
  });
}

export function useRenameFontFamily(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch<BrandFontFamilySlot>(`/brands/${brandId}/font-families/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(brandId) }),
  });
}

export function useDeleteFontFamily(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/brands/${brandId}/font-families/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(brandId) });
      qc.invalidateQueries({ queryKey: ["kit", brandId] });
    },
  });
}

// --- Upload helpers ---

export interface UploadFontFileInput {
  brandId: string;
  fontFamilyId: string;
  file: File;
  fontWeight: number;
  fontStyle: "normal" | "italic";
}

interface PresignedUpload {
  uploadUrl: string;
  publicUrl: string;
  key: string;
}

export async function uploadFontFile(
  input: UploadFontFileInput,
): Promise<{ id: string; url: string }> {
  const { brandId, fontFamilyId, file, fontWeight, fontStyle } = input;
  const presign = await apiFetch<PresignedUpload>(
    `/brands/${brandId}/upload-url?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
  );
  await fetch(presign.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  const asset = await apiFetch<{ id: string; url: string }>(`/brands/${brandId}/assets`, {
    method: "POST",
    body: JSON.stringify({
      url: presign.publicUrl,
      type: "FONT",
      filename: file.name,
      sizeBytes: file.size,
      mimeType: file.type,
      role: "FONT_FILE",
      fontFamilyId,
      fontWeight,
      fontStyle,
    }),
  });
  return asset;
}

export async function uploadLogoFile(
  brandId: string,
  file: File,
): Promise<{ id: string; url: string }> {
  const presign = await apiFetch<PresignedUpload>(
    `/brands/${brandId}/upload-url?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
  );
  await fetch(presign.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  const asset = await apiFetch<{ id: string; url: string }>(`/brands/${brandId}/assets`, {
    method: "POST",
    body: JSON.stringify({
      url: presign.publicUrl,
      type: "IMAGE",
      filename: file.name,
      sizeBytes: file.size,
      mimeType: file.type,
      role: "LOGO_PRIMARY",
    }),
  });
  return asset;
}
